"use client";

import StackOverflow from "@/assets/icons/StackOverflow";
import { useAppContext } from "@/providers/AppProvider";
import { motion } from "framer-motion";
import {
    Code2,
    Github,
    Hammer,
    Instagram,
    Linkedin,
    Mail,
    Twitter,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import PrimaryThemeSelector from "./ui/PrimaryThemeSelector";
import ThemeSwitchButton from "./ui/ThemeSwitchButton";
import Clock from "./widgets/Clock";
import MusicPlayer from "./widgets/MusicPlayer";
import Visits from "./widgets/Visits";

function Sidebar() {
    const { user } = useAppContext();
    const { name, role, image, email, socialLinks } = user || {};

    const contactItems: {
        icon: React.ReactNode;
        label: string;
        value: React.ReactNode;
        href?: string;
    }[] = [];

    if (email) {
        contactItems.push({
            icon: <Mail className="size-6 text-primary" />,
            label: "Email",
            value: email,
            href: "mailto:" + email,
        });
    }

    contactItems.push({
        icon: <Code2 className="size-6 text-primary" />,
        label: "Status",
        value: (
            <span className="flex items-center gap-2 font-semibold text-text-color">
                <span>Currently Coding</span>
            </span>
        ),
    });

    contactItems.push({
        icon: <Hammer className="size-6 text-primary" />,
        label: "Building",
        value: "HRM & CRM Platform",
    });

    return (
        <aside className="w-full bg-transparent md:h-full md:w-[350px]">
            <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="card relative flex h-full flex-col overflow-y-auto px-4 py-8 md:p-6"
            >
                <ThemeSwitchButton />
                {/* Profile Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex flex-col items-center gap-4 rounded-xl md:flex-row md:items-center md:gap-6 md:text-start"
                >
                    {/* Avatar */}
                    <div className="relative">
                        <Image
                            src={image || "https://github.com/sahilatahar.png"}
                            alt="Profile Image"
                            width={0}
                            height={0}
                            sizes="100vw"
                            priority
                            fetchPriority="high"
                            className="size-28 rounded-full border-4 border-primary object-cover shadow-md md:size-24"
                        />
                        <span className="absolute bottom-2 right-2 h-4 w-4 rounded-full border-2 border-white bg-green-500 md:bottom-1 md:right-1 dark:border-border-color"></span>
                    </div>
                    {/* Name & Role */}
                    <div className="text-center md:text-start">
                        <h1 className="text-xl font-bold text-primary md:text-2xl">
                            {name}
                        </h1>
                        <h2 className="text-muted-foreground text-sm">
                            {role}
                        </h2>
                        <p className="mt-1 text-xs font-medium text-green-500">
                            ● Online
                        </p>
                    </div>
                </motion.section>

                {/* Contact Info, Status & Building Section */}
                <motion.section
                    initial="hidden"
                    animate="visible"
                    variants={{
                        hidden: {},
                        visible: {
                            transition: {
                                staggerChildren: 0.15,
                            },
                        },
                    }}
                    className="flex cursor-default flex-wrap justify-start gap-6 pt-6 sm:justify-center md:justify-start"
                >
                    {contactItems.map((item, index) => {
                        const Wrapper = item.href ? Link : "div";
                        return (
                            <motion.div
                                key={index}
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 },
                                }}
                            >
                                <Wrapper
                                    href={item.href || "#"}
                                    className={`flex w-[250px] items-center gap-4 ${item.href ? "cursor-pointer" : ""}`}
                                >
                                    <div className="card flex items-center justify-center rounded-lg p-2">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground text-xs uppercase tracking-wider">
                                            {item.label}
                                        </p>
                                        <div className="break-all text-sm font-medium">
                                            {item.value}
                                        </div>
                                    </div>
                                </Wrapper>
                            </motion.div>
                        );
                    })}
                </motion.section>

                {/* Music Player Section (Centers equally in height between Building & Social Links) */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="my-auto flex flex-1 flex-col items-center justify-center py-6"
                >
                    <MusicPlayer />
                </motion.section>

                {/* Footer Section */}
                <motion.section
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="flex flex-col justify-end pt-4"
                >
                    <div className="flex items-center justify-evenly">
                        {socialLinks?.stackOverflow && (
                            <Link
                                href={socialLinks.stackOverflow}
                                aria-label="StackOverflow"
                                target="_blank"
                            >
                                <StackOverflow className="size-8 hover:text-primary" />
                            </Link>
                        )}
                        {socialLinks?.twitter && (
                            <Link
                                href={socialLinks.twitter}
                                aria-label="Twitter"
                                target="_blank"
                            >
                                <Twitter className="size-8 hover:text-primary" />
                            </Link>
                        )}
                        {socialLinks?.linkedin && (
                            <Link
                                href={socialLinks.linkedin}
                                aria-label="Linkedin"
                                target="_blank"
                            >
                                <Linkedin className="size-8 hover:text-primary" />
                            </Link>
                        )}
                        {socialLinks?.instagram && (
                            <Link
                                href={socialLinks.instagram}
                                aria-label="Instagram"
                                target="_blank"
                            >
                                <Instagram className="size-8 hover:text-primary" />
                            </Link>
                        )}
                        {socialLinks?.github && (
                            <Link
                                href={socialLinks.github}
                                aria-label="Github"
                                target="_blank"
                            >
                                <Github className="size-8 hover:text-primary" />
                            </Link>
                        )}
                    </div>
                    <Clock />
                    <Visits />
                    <PrimaryThemeSelector />
                </motion.section>
            </motion.div>
        </aside>
    );
}
export default Sidebar;
